import React, { useState, useMemo, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import SearchInput from '../../../components/dashboardcomponents/SearchComponents/SearchInput/SearchInput';
import SearchTabs from '../../../components/dashboardcomponents/SearchComponents/SearchTabs/SearchTabs';
import SearchFilters from '../../../components/dashboardcomponents/SearchComponents/SearchFilters/SearchFilters';
import SearchResults from '../../../components/dashboardcomponents/SearchComponents/SearchResults/SearchResults';
import { globalSearch, resetSearchState } from '../../../store/features/globalSearch';
import { fetchTags } from '../../../store/features/tagsApi';
import { ListCoRoleName } from '../../../store/features/ListCoUserByRole';
import {
    SEARCH_FILTER_OPTIONS,
    KB_STATUSES,
} from '../../../constants/searchDummyData';
import SearchSkeleton from '../../../components/dashboardcomponents/SearchComponents/SearchSkeleton/SearchSkeleton';
import { isHeaderInlinePageTitleEnabled } from '../../../config/featureFlags';

const SORT_BY_PARAMS = {
    'Relevance': 'relevance',
    'Newest': 'newest',
    'Oldest': 'oldest',
    'Recently Updated': 'recentlyUpdated',
    'Most Viewed': 'mostViewed',
};

const SEARCH_TIMEFRAME_OPTIONS = [
    { label: 'Today', id: 'today' },
    { label: 'Yesterday', id: 'yesterday' },
    { label: 'Last 7 days', id: 'last7days' },
    { label: 'Last 30 days', id: 'last30days' },
    { label: 'Last year', id: 'lastyear' },
    { label: 'Custom', id: 'custom' },
];

const initialFilters = {
    assignee: '',
    status: '',
    kbTags: '',
    chatTags: '',
    ticketTags: '',
    type: '',
    timeframe: '',
    customStartDate: '',
    customEndDate: '',
    moreFilters: [],
    sortBy: '',
    sortOrder: -1,
};

function Search() {
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [filters, setFilters] = useState(initialFilters);
    const [activeSearchFlags, setActiveSearchFlags] = useState([]);
    const [suggestionOptions, setSuggestionOptions] = useState([]);
    const [pendingFlagType, setPendingFlagType] = useState(null);
    const [isWaitingForResults, setIsWaitingForResults] = useState(false);
    const [agentsList, setAgentsList] = useState([]);
    const [openCustomDatePicker, setOpenCustomDatePicker] = useState(false);
    const searchAbortRef = useRef(null);
    const timerRef = useRef(null);
    const searchQueryRef = useRef(searchQuery);
    const filtersRef = useRef(filters);

    searchQueryRef.current = searchQuery;
    filtersRef.current = filters;

    const { results, loading } = useSelector((state) => state.globalSearch);
    const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
    const tagsList = useSelector((state) => state.tags?.list || []);

    const kbTagsData = useMemo(
        () => tagsList.filter((t) => t.type === 'knowledge_base'),
        [tagsList],
    );
    const conversationTagsData = useMemo(
        () => tagsList.filter((t) => t.type === 'conversation'),
        [tagsList],
    );

    useEffect(() => {
        if (businessId) {
            dispatch(fetchTags({ businessId, type: 'all', search: '' }));
        }
        dispatch(ListCoRoleName({ name: 'agent,admin,admin_with_billing' })).then((res) => {
            if (res?.payload?.status === 200 && res?.payload?.data) {
                setAgentsList(res.payload.data);
            }
        });
        return () => {
            dispatch(resetSearchState());
        };
    }, [businessId, dispatch]);

    useEffect(() => {
        if (filters.timeframe !== 'custom') setOpenCustomDatePicker(false);
    }, [filters.timeframe]);

    useEffect(() => {
        if (filters.sortBy) {
            setActiveSearchFlags((prev) => {
                const hasSort = prev.some((f) => f.type === 'sortBy');
                if (hasSort) return prev.map((f) => (f.type === 'sortBy' ? { ...f, value: filters.sortBy, isPending: false } : f));
                return [...prev, { type: 'sortBy', label: 'Sort by', value: filters.sortBy, isPending: false }];
            });
        } else {
            setActiveSearchFlags((prev) => prev.filter((f) => f.type !== 'sortBy'));
        }
    }, [filters.sortBy]);

    useEffect(() => {
        if (searchAbortRef.current) {
            searchAbortRef.current.abort();
        }
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        const { sortBy, sortOrder, moreFilters, ...filterValues } = filters;
        const hasValue = (v) => v && typeof v === 'string' && v.length > 0;
        const hasFilterValues = Object.entries(filterValues).some(([key, v]) => {
            if (key === 'timeframe' && v === 'custom') {
                return hasValue(filters.customStartDate) && hasValue(filters.customEndDate);
            }
            return hasValue(v);
        });
        const hasSort = !!sortBy;
        const hasSearchParams = searchQuery.trim() || hasFilterValues || hasSort;

        if (!hasSearchParams) {
            setIsWaitingForResults(false);
            dispatch(resetSearchState());
            return;
        }
        setIsWaitingForResults(true);

        timerRef.current = setTimeout(() => {
            const currentQuery = searchQueryRef.current;
            const currentFilters = filtersRef.current;

            if (currentFilters.timeframe === 'custom' && (!currentFilters.customStartDate || !currentFilters.customEndDate)) {
                setIsWaitingForResults(false);
                return;
            }

            const params = {
                query: currentQuery,
                kbTags: currentFilters.kbTags,
                chatTags: currentFilters.chatTags,
                ticketTags: currentFilters.ticketTags,
                status: currentFilters.status,
                assignee: currentFilters.assignee,
                type: currentFilters.type,
                timeframe: currentFilters.timeframe,
                sortBy: SORT_BY_PARAMS[currentFilters.sortBy] ?? 'relevance',
                sortOrder: currentFilters.sortOrder,
                page: 1,
            };
            if (currentFilters.timeframe === 'custom') {
                if (currentFilters.customStartDate) params.startDate = currentFilters.customStartDate;
                if (currentFilters.customEndDate) params.endDate = currentFilters.customEndDate;
            }

            const promise = dispatch(globalSearch(params));
            searchAbortRef.current = promise;
            setIsWaitingForResults(false);
        }, 500);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [searchQuery, filters, dispatch]);

    const showSkeleton = isWaitingForResults || loading;

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const getOptionsForType = (type) => {
        switch (type) {
            case 'assigned':
            case 'assignee':
                return agentsList.map(u => ({ label: u.full_name, id: u._id }));
            case 'status':
                return SEARCH_FILTER_OPTIONS.statuses || [];
            case 'tags': {
                const kb = kbTagsData.map((t) => ({ label: t.name, id: t._id, tagType: 'kbTags' }));
                const conversation = conversationTagsData.map((t) => ({
                    label: t.name,
                    id: t._id,
                    tagType: 'chatTags',
                }));
                return [...kb, ...conversation];
            }
            case 'kbTags':
                return kbTagsData.map((t) => ({ label: t.name, id: t._id }));
            case 'chatTags':
            case 'ticketTags':
                return conversationTagsData.map((t) => ({ label: t.name, id: t._id }));
            case 'type':
                return SEARCH_FILTER_OPTIONS.types || [];
            case 'timeframe':
                return SEARCH_TIMEFRAME_OPTIONS;
            default:
                return [];
        }
    };

    const handleAddFlag = (type) => {
        const options = getOptionsForType(type);
        setSuggestionOptions(options);
        setPendingFlagType(type);

        const labelMap = { assignee: 'Assigned', tags: 'Tags', kbTags: 'KB Tag', chatTags: 'Chat Tag', ticketTags: 'Ticket Tag', status: 'Status', type: 'Type', timeframe: 'Timeframe' };
        const displayLabel = labelMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
        if (!activeSearchFlags.find(f => f.type === type)) {
            setActiveSearchFlags([...activeSearchFlags, { type, label: displayLabel, isPending: true }]);
        } else {
            setActiveSearchFlags(activeSearchFlags.map(f => f.type === type ? { ...f, isPending: true } : f));
        }
    };

    const handleSelectSuggestion = (suggestion) => {
        if (!pendingFlagType) return;

        const value = typeof suggestion === 'object' ? (suggestion.displayLabel ?? suggestion.label) : suggestion;
        const id = typeof suggestion === 'object' ? suggestion.id : suggestion;
        const tagType = typeof suggestion === 'object' ? suggestion.tagType : null;

        if (pendingFlagType === 'tags' && tagType) {
            setActiveSearchFlags(prev => prev.map(f =>
                f.type === 'tags' ? { ...f, value, isPending: false } : f
            ));
            setFilters(prev => {
                const next = { ...prev, kbTags: '', chatTags: '', ticketTags: '' };
                next[tagType] = id;
                if (tagType === 'chatTags') next.ticketTags = id;
                return next;
            });
        } else {
            setActiveSearchFlags(prev => prev.map(f =>
                f.type === pendingFlagType
                    ? { ...f, value, isPending: false }
                    : f
            ));
            handleFilterChange(pendingFlagType, id);
        }

        if (pendingFlagType === 'timeframe' && id === 'custom') {
            setOpenCustomDatePicker(true);
        }

        setSuggestionOptions([]);
        setPendingFlagType(null);
    };

    const handleRemoveFlag = (flag) => {
        if (flag.type === 'sortBy') {
            setActiveSearchFlags((prev) => prev.filter((f) => f.type !== 'sortBy'));
            handleFilterChange('sortBy', '');
            if (pendingFlagType === 'sortBy') {
                setSuggestionOptions([]);
                setPendingFlagType(null);
            }
            return;
        }
        if (flag.type === 'tags') {
            if (flag.value) {
                setActiveSearchFlags(prev => prev.map(f =>
                    f.type === 'tags' ? { ...f, value: undefined, isPending: true } : f
                ));
                setFilters(prev => ({ ...prev, kbTags: '', chatTags: '', ticketTags: '' }));
                setSuggestionOptions(getOptionsForType('tags'));
                setPendingFlagType('tags');
            } else {
                setActiveSearchFlags(activeSearchFlags.filter(f => f.type !== 'tags'));
                setFilters(prev => ({ ...prev, kbTags: '', chatTags: '', ticketTags: '' }));
                if (pendingFlagType === 'tags') {
                    setSuggestionOptions([]);
                    setPendingFlagType(null);
                } else {
                    setSuggestionOptions(getOptionsForType('tags'));
                    setPendingFlagType('tags');
                }
            }
            return;
        }
        if (flag.value) {
            setActiveSearchFlags(prev => prev.map(f =>
                f.type === flag.type ? { ...f, value: undefined, isPending: true } : f
            ));
            handleFilterChange(flag.type, '');

            const options = getOptionsForType(flag.type);
            setSuggestionOptions(options);
            setPendingFlagType(flag.type);
        } else {
            setActiveSearchFlags(activeSearchFlags.filter(f => f.type !== flag.type));
            handleFilterChange(flag.type, '');

            if (flag.type === pendingFlagType) {
                setSuggestionOptions([]);
                setPendingFlagType(null);
            }
        }
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        if (pendingFlagType) {
            setSuggestionOptions([]);
            setActiveSearchFlags((prev) => prev.filter((f) => f.type !== pendingFlagType));
            if (pendingFlagType === 'tags') {
                setFilters(prev => ({ ...prev, kbTags: '', chatTags: '', ticketTags: '' }));
            } else {
                handleFilterChange(pendingFlagType, '');
            }
            setPendingFlagType(null);
        }
    };

    const handleClear = () => {
        setSearchQuery('');
        setFilters(initialFilters);
        setActiveSearchFlags([]);
        setSuggestionOptions([]);
        setPendingFlagType(null);
        setOpenCustomDatePicker(false);
        setActiveTab('all');
        dispatch(resetSearchState());
    };

    const hasActiveFilters = useMemo(() => {
        const { sortBy, sortOrder, moreFilters, ...rest } = filters;
        return Object.values(rest).some(v => v);
    }, [filters]);

    const hasSort = useMemo(() => {
        return !!filters.sortBy;
    }, [filters.sortBy]);

    const chats = results?.data?.chats?.items || [];
    const tickets = results?.data?.tickets?.items || [];
    const articles = results?.data?.kb?.items || [];

    const counts = {
        all: (results?.data?.chats?.total || 0) + (results?.data?.tickets?.total || 0) + (results?.data?.kb?.total || 0),
        chats: results?.data?.chats?.total || 0,
        tickets: results?.data?.tickets?.total || 0,
        knowledgeBase: results?.data?.kb?.total || 0,
        recent: null,
    };

    const hasQuery = searchQuery.trim().length > 0 || hasActiveFilters || activeSearchFlags.length > 0 || hasSort || loading || !!results;

    return (
        <Box sx={{ padding: { xs: '16px', md: '24px' }, paddingTop: { md: '16px' } }}>
            {/* Legacy in-page title — hidden when header shows inline title (Application V2) */}
            {!isHeaderInlinePageTitleEnabled && (
            <Typography sx={{
                color: '#061522',
                fontFamily: 'Inter',
                fontSize: '24px',
                fontWeight: 500,
                lineHeight: '18px',
                marginBottom: '24px',
            }}>
                Search
            </Typography>
            )}

            <Box sx={{ marginBottom: '16px' }}>
                <SearchInput
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClear={handleClear}
                    activeFilters={activeSearchFlags}
                    onRemoveFilter={handleRemoveFlag}
                    suggestionOptions={suggestionOptions}
                    onSelectSuggestion={handleSelectSuggestion}
                />
            </Box>

            <SearchFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onAddFilter={handleAddFlag}
                openCustomDatePicker={openCustomDatePicker}
                onCustomDatePickerOpenChange={setOpenCustomDatePicker}
            />

            {hasQuery && (
                <>
                    <SearchTabs
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        counts={counts}
                    />

                    {showSkeleton ? (
                        <SearchSkeleton activeTab={activeTab} hideChats={KB_STATUSES.includes(filters.status)} />
                    ) : (
                        <SearchResults
                            chats={chats}
                            tickets={tickets}
                            articles={articles}
                            activeTab={activeTab}
                            searchQuery={searchQuery}
                            hideChats={KB_STATUSES.includes(filters.status)}
                        />
                    )}
                </>
            )}
        </Box>
    );
}

export default Search;
